import React, { useEffect, useState } from 'react'
import { Box, Card, CardActionArea, CardContent, CircularProgress, Typography } from '@mui/material'
import PeopleIcon  from '@mui/icons-material/People'
import SchoolIcon  from '@mui/icons-material/School'
import ArticleIcon from '@mui/icons-material/Article'
import PendingIcon from '@mui/icons-material/HourglassEmpty'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../supabaseClient'
import { useAuth } from '../../../auth/AuthContext'
import MeetingsPanel from '../../../components/staff/MeetingsPanel'
import PendingSubmissionsPanel from '../../../components/staff/PendingSubmissionsPanel'
import StudentQuestionsPanel from '../../../components/mentor/StudentQuestionsPanel'

interface Stats {
  students: number
  classes:  number
  projects: number
  pending:  number
}

function StatCard({ label, value, icon, to }: {
  label: string; value: number; icon: React.ReactNode; to: string
}) {
  const navigate = useNavigate()
  return (
    <Card variant="outlined" sx={{ flex: '1 1 200px' }}>
      <CardActionArea onClick={() => navigate(to)}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default function AdminDashboard() {
  const { profile } = useAuth()
  const [stats,   setStats]   = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [
        { count: students },
        { count: classes },
        { count: projects },
        { count: pending },
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }).eq('role', 'student'),
        supabase.from('class').select('*', { count: 'exact', head: true }),
        supabase.from('project').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('users').select('*', { count: 'exact', head: true }).eq('status', 'pending_activation'),
      ])
      setStats({
        students: students ?? 0,
        classes:  classes  ?? 0,
        projects: projects ?? 0,
        pending:  pending  ?? 0,
      })
      setLoading(false)
    }
    load()
  }, [])

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Dashboard</Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {profile?.first_name}.
      </Typography>

      <StudentQuestionsPanel />

      <PendingSubmissionsPanel />

      <MeetingsPanel />

      {loading ? (
        <CircularProgress />
      ) : stats ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <StatCard label="Students"         value={stats.students} icon={<PeopleIcon />}  to="/students" />
          <StatCard label="Classes"          value={stats.classes}  icon={<SchoolIcon />}  to="/classes" />
          <StatCard label="Active Projects"  value={stats.projects} icon={<ArticleIcon />} to="/students" />
          <StatCard label="Pending Accounts" value={stats.pending}  icon={<PendingIcon />} to="/staff" />
        </Box>
      ) : null}
    </Box>
  )
}
